import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getPublicEventTicketStorefront, requestPublicEventTicketTransfer } from '../../../services/eventApi';
import LoadingSpinner from '../../Common/LoadingSpinner';

const PublicEventTicketTransferRequestPage = () => {
  const { eventId } = useParams();
  const [loading, setLoading] = useState(true);
  const [eventName, setEventName] = useState('');
  const [transfersEnabled, setTransfersEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    holderEmail: '',
    ticketCode: '',
    toName: '',
    toEmail: '',
    toPhone: '',
    note: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getPublicEventTicketStorefront(eventId);
        if (!cancelled) {
          setEventName(res.data?.data?.event?.name || 'Event');
          setTransfersEnabled(Boolean(res.data?.data?.transfersEnabled));
        }
      } catch {
        if (!cancelled) toast.error('Ticketing unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await requestPublicEventTicketTransfer(eventId, form);
      const out = res.data?.data;
      setResult(out);
      toast.success('Transfer link created — recipient will receive an email');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not start transfer');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-100">
        <LoadingSpinner />
      </div>
    );
  }

  if (!transfersEnabled) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-100 p-6 text-center text-slate-600">
        Transfers are not enabled for this event.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 px-4 py-10">
      <div className="mx-auto max-w-lg rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wider text-amber-800/70">Ticket transfer</p>
        <h1 className="mt-1 font-serif text-2xl text-stone-900">{eventName}</h1>
        <p className="mt-2 text-sm text-stone-600">
          Transfer a ticket you hold to another person. They will receive a link to accept the transfer.
        </p>

        {result?.transferUrl ? (
          <div className="mt-5 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            <p className="font-medium">Transfer link ready</p>
            <p className="mt-2 break-all text-xs">
              <a className="underline" href={result.transferUrl}>{result.transferUrl}</a>
            </p>
            <p className="mt-2 text-xs text-emerald-800">An email was also sent to the recipient when possible.</p>
          </div>
        ) : (
          <form className="mt-5 space-y-3" onSubmit={submit}>
            <label className="block text-xs text-stone-600">
              Your email (on the ticket)
              <input
                required
                type="email"
                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
                placeholder="you@email.com"
                value={form.holderEmail}
                onChange={(e) => setForm((s) => ({ ...s, holderEmail: e.target.value }))}
              />
            </label>
            <label className="block text-xs text-stone-600">
              Ticket code
              <input
                required
                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm font-mono uppercase"
                placeholder="EVT-TKT-000001"
                value={form.ticketCode}
                onChange={(e) => setForm((s) => ({ ...s, ticketCode: e.target.value.toUpperCase() }))}
              />
            </label>
            <label className="block text-xs text-stone-600">
              Recipient name
              <input
                required
                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
                placeholder="Recipient full name"
                value={form.toName}
                onChange={(e) => setForm((s) => ({ ...s, toName: e.target.value }))}
              />
            </label>
            <label className="block text-xs text-stone-600">
              Recipient email
              <input
                required
                type="email"
                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
                placeholder="recipient@email.com"
                value={form.toEmail}
                onChange={(e) => setForm((s) => ({ ...s, toEmail: e.target.value }))}
              />
            </label>
            <label className="block text-xs text-stone-600">
              Recipient phone (optional)
              <input
                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
                placeholder="+94 77 123 4567"
                value={form.toPhone}
                onChange={(e) => setForm((s) => ({ ...s, toPhone: e.target.value }))}
              />
            </label>
            <label className="block text-xs text-stone-600">
              Note (optional)
              <textarea
                rows={2}
                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
                placeholder="Optional message for the recipient"
                value={form.note}
                onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))}
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-stone-900 py-2.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {busy ? 'Creating…' : 'Create transfer link'}
            </button>
          </form>
        )}

        <p className="mt-5 text-center text-xs text-stone-500">
          <Link className="underline" to={`/public/events/${eventId}/tickets`}>Back to tickets</Link>
        </p>
      </div>
    </div>
  );
};

export default PublicEventTicketTransferRequestPage;
